import { useState, type FormEvent } from 'react';
import type { ContractorPayment, CreateContractorPaymentInput } from '@dsr/shared-types';
import {
  use1099Summary,
  useContractorPayments,
  useCreateContractorPayment,
  useLookup,
  useProjects,
  useVoidContractorPayment,
} from '../api/queries/financials';
import { usePersonnelList } from '../api/queries/personnel';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import { can } from '../auth/store';
import { ApiError } from '../api/client';
import { fmtDate } from '../utils/date';
import { fmtMoney } from '../utils/money';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  StatusBadge,
  Table,
  Textarea,
} from '../components/ui';

const MONEY_RE = /^\d+(\.\d{1,2})?$/;
const THIS_YEAR = new Date().getFullYear();
const YEARS = [THIS_YEAR, THIS_YEAR - 1, THIS_YEAR - 2];

export function ContractorPaymentsPage(): JSX.Element {
  const [taxYear, setTaxYear] = useState<number>(THIS_YEAR);
  const [view, setView] = useState<'ledger' | '1099'>('ledger');
  const [createOpen, setCreateOpen] = useState(false);
  const [voiding, setVoiding] = useState<ContractorPayment | null>(null);

  const { data, isLoading, isError } = useContractorPayments({ tax_year: taxYear });
  const { data: summary } = use1099Summary(taxYear);
  const doVoid = useVoidContractorPayment();

  const canWrite = can('contractor_payment.write');
  const canVoid = can('contractor_payment.void');
  const rows = data?.contractor_payments ?? [];

  return (
    <div className="page contractor-payments-page">
      <header className="page-header">
        <div>
          <h1>Contractor Payments</h1>
          <p className="page-sub">1099 money out — payments to contract personnel.</p>
        </div>
        {canWrite && (
          <div className="page-toolbar">
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              Record payment
            </Button>
          </div>
        )}
      </header>

      <div className="filters-bar">
        <select
          className="form-input"
          value={taxYear}
          onChange={(e) => setTaxYear(Number(e.target.value))}
        >
          {YEARS.map((y) => (
            <option key={y} value={y}>Tax year {y}</option>
          ))}
        </select>
        <Button variant={view === 'ledger' ? 'primary' : 'ghost'} onClick={() => setView('ledger')}>
          Ledger
        </Button>
        <Button variant={view === '1099' ? 'primary' : 'ghost'} onClick={() => setView('1099')}>
          1099 summary
        </Button>
      </div>

      {view === '1099' ? (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Contractor</Table.HeaderCell>
              <Table.HeaderCell>Payments</Table.HeaderCell>
              <Table.HeaderCell>Paid total ({taxYear})</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {(summary?.rows ?? []).map((r) => (
              <Table.Row key={r.personnel_id}>
                <Table.Cell tone="strong">{r.personnel_name}</Table.Cell>
                <Table.Cell>{r.payment_count}</Table.Cell>
                <Table.Cell tone="strong">{fmtMoney(r.paid_total)}</Table.Cell>
              </Table.Row>
            ))}
            {(summary?.rows ?? []).length === 0 && (
              <Table.Empty colSpan={3}>No paid contractor payments in {taxYear}.</Table.Empty>
            )}
          </Table.Body>
        </Table>
      ) : (
        <>
          {isLoading && <p className="muted">Loading…</p>}
          {isError && <p className="error-text" role="alert">Failed to load contractor payments.</p>}
          {!isLoading && !isError && rows.length === 0 && (
            <p className="muted">No contractor payments in {taxYear}.</p>
          )}
          {rows.length > 0 && (
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>Contractor</Table.HeaderCell>
                  <Table.HeaderCell>Project</Table.HeaderCell>
                  <Table.HeaderCell>Method</Table.HeaderCell>
                  <Table.HeaderCell>Amount</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  {canVoid && <Table.HeaderCell aria-label="Actions" />}
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {rows.map((cp) => (
                  <Table.Row key={cp.id}>
                    <Table.Cell>{fmtDate(cp.payment_date)}</Table.Cell>
                    <Table.Cell tone="strong">{cp.personnel_name ?? '—'}</Table.Cell>
                    <Table.Cell tone="muted">{cp.project_title ?? '—'}</Table.Cell>
                    <Table.Cell>
                      {cp.method}
                      {cp.reference_no && <div className="muted small">{cp.reference_no}</div>}
                    </Table.Cell>
                    <Table.Cell>{fmtMoney(cp.amount)}</Table.Cell>
                    <Table.Cell><StatusBadge status={cp.status}>{cp.status}</StatusBadge></Table.Cell>
                    {canVoid && (
                      <Table.Cell>
                        {cp.status !== 'void' && (
                          <Button variant="ghost" onClick={() => setVoiding(cp)}>
                            Void
                          </Button>
                        )}
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </>
      )}

      {createOpen && <RecordContractorPaymentModal onClose={() => setCreateOpen(false)} />}
      {voiding && (
        <ConfirmDialog
          open
          title="Void contractor payment"
          message={`Void the ${fmtMoney(voiding.amount)} payment to ${voiding.personnel_name ?? 'contractor'}? It will stop counting toward ${voiding.tax_year} 1099 totals.`}
          confirmLabel="Void"
          danger
          busy={doVoid.isPending}
          onConfirm={() => {
            doVoid
              .mutateAsync(voiding.id)
              .then(() => toast.success('Contractor payment voided.'))
              .catch((err) =>
                toast.danger(err instanceof ApiError ? err.message : 'Void failed.'),
              )
              .finally(() => setVoiding(null));
          }}
          onCancel={() => setVoiding(null)}
        />
      )}
    </div>
  );
}

function RecordContractorPaymentModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [personnelId, setPersonnelId] = useState<number | ''>('');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState<number | null>(Date.now());
  const [method, setMethod] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [markPaid, setMarkPaid] = useState(true);
  const [notes, setNotes] = useState('');

  const { data: personnelData } = usePersonnelList({});
  const { data: projectsData } = useProjects({});
  const { data: methods } = useLookup('payment_method');
  const create = useCreateContractorPayment();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (personnelId === '' || !date || !MONEY_RE.test(amount)) return;
    const input: CreateContractorPaymentInput = {
      personnel_id: personnelId,
      amount,
      payment_date: date,
      status: markPaid ? 'paid' : 'pending',
    };
    if (projectId !== '') input.project_id = projectId;
    if (method) input.method = method;
    if (referenceNo.trim()) input.reference_no = referenceNo.trim();
    if (notes.trim()) input.notes = notes.trim();

    try {
      await create.mutateAsync(input);
      toast.success('Contractor payment recorded.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to record payment.');
    }
  };

  return (
    <Dialog
      title="Record contractor payment"
      open
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="record-cp-form"
            variant="primary"
            disabled={create.isPending || personnelId === '' || !date || !MONEY_RE.test(amount)}
          >
            {create.isPending ? 'Saving…' : 'Record'}
          </Button>
        </>
      }
    >
      <form id="record-cp-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Contractor" required>
          <select
            className="form-input"
            value={personnelId === '' ? '' : String(personnelId)}
            onChange={(e) => setPersonnelId(e.target.value === '' ? '' : Number(e.target.value))}
            required
          >
            <option value="">Select personnel…</option>
            {(personnelData?.personnel ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.legal_name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Project">
          <select
            className="form-input"
            value={projectId === '' ? '' : String(projectId)}
            onChange={(e) => setProjectId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">None</option>
            {(projectsData?.projects ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Amount" required>
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="850.00"
            inputMode="decimal"
            required
          />
        </FormField>
        <FormField label="Date" required>
          <DateInput value={date} onChange={setDate} required />
        </FormField>
        <FormField label="Method">
          <select className="form-input" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">Other</option>
            {(methods?.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Reference #">
          <Input
            value={referenceNo}
            onChange={(e) => setReferenceNo(e.target.value)}
          />
        </FormField>
        <label className="form-field form-field-full" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={markPaid} onChange={(e) => setMarkPaid(e.target.checked)} />
          <span>Already paid (counts toward 1099 totals immediately)</span>
        </label>
        <FormField label="Notes" full>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
      </form>
    </Dialog>
  );
}
